/**
 * Лист снизу для телефона: «Ещё» в нижней панели, фильтры отчёта.
 *
 * Ручка 36×4, закрытие свайпом вниз, по затемнению и по Escape.
 * Высота — по содержимому, не больше 88% экрана. Въезжает transform'ом
 * (--dur-enter), уходит быстрее (--dur-leave); при reduced motion — fade.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'
import { durationToken } from '../finance/motion'
import { useDialog } from './useDialog'

export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const drag = useRef<{ start: number; delta: number } | null>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)))
      return () => cancelAnimationFrame(id)
    }
    setShown(false)
    const id = window.setTimeout(() => setMounted(false), durationToken('--dur-leave', 200))
    return () => window.clearTimeout(id)
  }, [open])

  // Фокус — в лист при открытии и обратно на кнопку «Ещё» / «Фильтры»
  // при закрытии; Tab не уходит на страницу под затемнением.
  useDialog(panel, { active: open, onClose })

  if (!mounted) return null

  const start = (event: React.TouchEvent) => {
    drag.current = { start: event.touches[0]!.clientY, delta: 0 }
    if (panel.current) panel.current.style.transition = 'none'
  }
  const move = (event: React.TouchEvent) => {
    if (!drag.current || !panel.current) return
    drag.current.delta = Math.max(0, event.touches[0]!.clientY - drag.current.start)
    panel.current.style.transform = `translateY(${drag.current.delta}px)`
  }
  const end = () => {
    const node = panel.current
    if (!drag.current || !node) return
    const { delta } = drag.current
    drag.current = null
    if (delta > node.getBoundingClientRect().height / 4) {
      // Уезжает вниз от точки, где отпустили: без отскока вверх перед уходом.
      node.style.transition = 'transform var(--dur-leave) var(--ease-exit)'
      node.style.transform = 'translateY(100%)'
      onClose()
    } else {
      node.style.transition = ''
      node.style.transform = ''
    }
  }

  return createPortal(
    <div className={`sheet-layer${shown ? ' sheet-layer--open' : ''}`}>
      <div className="sheet-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={panel} className="sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className="sheet__grip" onTouchStart={start} onTouchMove={move} onTouchEnd={end} aria-hidden="true">
          <span className="sheet__handle" />
        </div>
        <header className="sheet__header" onTouchStart={start} onTouchMove={move} onTouchEnd={end}>
          <h2 className="sheet__title">{title}</h2>
          <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" />
          </button>
        </header>
        <div className="sheet__body">{children}</div>
        {footer && <div className="sheet__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
